import React from 'react';

import {useFocusOnElement} from '@optimaxdev/analytics/desktop/hooks/useFocusOnElement/useFocusOnElement';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton} from '@optimaxdev/design-system';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';

import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';
import {flags} from 'libs/rollout';

import {CompleteOrderType} from 'types/completeOrderType';

import {PAYMENT_CODE_MESSAGE, SUBSCRIPTION_LOCATION} from '../../constants/checkout';
import {PAYMENTS} from '../../constants/paymentStep';
import {setPayerOptions} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {
    getCompleteOrderFirstErrorCode,
    getCompleteOrderResult,
    getIsPlacingOrder,
} from '../../store/selectors/completeOrder/completeOrder';
import {getNameSelectedMethod, getPayerInfo} from '../../store/selectors/paymentStep/paymentStep';

import {INSUFFICIENT_FUNDS} from '../bnplOfferPopup/bnplOfferPupupList/constant';
import {PlaceOrderErrors} from '../placeOrderErrors/placeOrderErrors';

import {sendEventForPlaceSubscribe} from './utils/utils';

import s from './placeOrder.scss';

export type PropsType = {
    isLoading: boolean;
    onSubmit?: () => void;
    completeOrder: CompleteOrderType;
    namePaymentMethod: PAYMENTS;
    isPlacingOrder: boolean;
    isSubscribe?: boolean;
    completeOrderErrorCode: string;
};

/**
 * Place order for checkout
 *
 * @constructor
 */
export const PlaceOrder: React.FC<PropsType> = ({
    completeOrder,
    completeOrderErrorCode,
    isLoading,
    isPlacingOrder,
    isSubscribe,
    namePaymentMethod,
    onSubmit = () => {},
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {isOneStepAvailable} = useIsOneStepAvailable();
    const {email} = useGetCustomerData();

    const sendAnalyticsCallback = () => {
        const paymentName = PAYMENT_CODE_MESSAGE[namePaymentMethod] || 'Other';

        sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
            message: `CTA - Place Order - ${paymentName} - Hover`,
            method: 'Payment',
            version: 'D',
        });
    };

    const {handler, ref} = useFocusOnElement({
        callBack: sendAnalyticsCallback,
    });

    const onFormSend = async () => {
        if (onSubmit) onSubmit();
        if (isSubscribe) await sendEventForPlaceSubscribe(email, SUBSCRIPTION_LOCATION.checkout);
    };

    const errors = [...((completeOrder.error && completeOrder.errors) || [])];

    const showError =
        Boolean(errors.length) &&
        !(
            completeOrderErrorCode === INSUFFICIENT_FUNDS &&
            flags.checkoutInsufficientFundsOffer.isEnabled()
        );

    const getButtonText = () => {
        return isFacelift ? 'Place My Order' : 'Place Order';
    };

    return (
        <>
            <section className={classNames(s.wrapper, {[s.wrapperFacelift]: isFacelift})}>
                <span ref={ref}>
                    <CtaButton
                        ariaLabel="Place Order"
                        className={s.button}
                        isDisabled={isLoading}
                        isLoading={isLoading || isPlacingOrder}
                        onClick={onFormSend}
                        onHover={handler}
                        size="medium"
                        type="button"
                    >
                        {getButtonText()}
                    </CtaButton>
                </span>
            </section>

            {showError && !isOneStepAvailable && (
                <PlaceOrderErrors errorComponentType="NOTIFICATION_BAR" errors={errors} />
            )}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    completeOrder: getCompleteOrderResult(store),
    completeOrderErrorCode: getCompleteOrderFirstErrorCode(store),
    isPlacingOrder: getIsPlacingOrder(store),
    isSubscribe: getPayerInfo(store).options.subscribe,
    namePaymentMethod: getNameSelectedMethod(store),
});

const mapDispatchToProps = {
    changeSubscribeOption: setPayerOptions,
};

export const PlaceOrderConnected = connect(mapStateToProps, mapDispatchToProps)(PlaceOrder);
